import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { colors } from "brand";
import { dropFvdPlaceholderTestName } from "score-common";
import PageShell from "../components/PageShell";
import { fetchScoreSheet } from "../api";
import { trackEvent } from "../analytics";
import { groupRidesByDay } from "../util/groupRidesByDay";

const POLL_INTERVAL_MS = 15000;

// #944 — mockup badge ("First Place", "Second Place", ...) for a numeric
// placing. A fixed navy-on-white chip (not a MUI palette color) reads the
// same in both themes — a filled chip doesn't fall prey to the primary-grey-
// on-dark contrast trap the rest of this app avoids.
const PLACING_BADGE_BG = "#1a2b5c";
const ORDINAL_WORDS = [
  null,
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
];
function placingLabel(placing) {
  const n = Number(placing);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < ORDINAL_WORDS.length) return `${ORDINAL_WORDS[n]} Place`;
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
  return `${n}${suffix} Place`;
}

// Card #2177 — report-api sends `percentage: null` (never a bare 0) for a
// ride whose score hasn't landed yet: apps/report-api/src/workers/
// generate-rider-sheets.js:100 (`percentage: data?.overallPercentage ?? null`)
// and packages/report-defs/src/score/competitor-score-sheet.js's unjudged
// branch both `?? null`. The bug: formatPercentage's old `Number.isFinite
// (Number(x))` check could not tell "no score yet" (null) from "genuinely
// scored zero" (0) — `Number(null)` is 0, which is also finite — so it
// silently painted 0.000% for both. A `typeof` guard on the raw value fixes
// this boundary: only an actual number reaches toFixed, so a real 0 still
// renders "0.000%" (AC3) while null returns null and the caller falls back
// to pending (AC2) instead of a fabricated score.
function formatPercentage(percentage) {
  return typeof percentage === "number" && Number.isFinite(percentage)
    ? `${percentage.toFixed(3)}%`
    : null;
}

// Card #971 — View/Download point at this app's own /open interstitial
// (never a raw S3 URL): report-api only tells us WHICH variants exist
// (`availableVariants`), and the interstitial mints a fresh, short-lived
// presigned URL right when the rider clicks, not when this page loaded.
function openHref({ requestId, rideId, variant, available }) {
  return available
    ? `/open/${requestId}/${rideId}/${variant}`
    : undefined;
}

function RideCard({ ride, includeComments, requestId }) {
  const available = ride.availableVariants || {};
  const htmlVariant = includeComments ? "htmlComments" : "htmlNoComments";
  const pdfVariant = includeComments ? "pdfComments" : "pdfNoComments";
  const htmlHref = openHref({
    requestId,
    rideId: ride.rideId,
    variant: htmlVariant,
    available: available[htmlVariant],
  });
  const pdfHref = openHref({
    requestId,
    rideId: ride.rideId,
    variant: pdfVariant,
    available: available[pdfVariant],
  });

  // #1837 — never render foxbox's "FVD Test <id>" missing-test-map breadcrumb
  // on this public rider portal.
  const testName = dropFvdPlaceholderTestName(ride.testName);
  const testOrClassName =
    testName || ride.className || `Ride #${ride.rideNumber}`;
  const title = ride.classNumber != null
    ? `CL ${ride.classNumber}: ${testOrClassName}`
    : testOrClassName;

  // "Thursday · July 16, 2026" in the mockup vs. the abbreviated short-form
  // date/time report-api already computes for the rendered scoresheet itself
  // (`ride.date`/`ride.time`, e.g. "Jul 17, 2026" / "11:49 AM") — reusing
  // that keeps this card's date guaranteed consistent with the sheet a rider
  // opens, rather than re-deriving a weekday off a re-parsed date string.
  const dateTimeBits = [ride.date, ride.time].filter(Boolean);

  const riderOnHorse = [ride.riderName, ride.horseName].filter(Boolean).join(" on ");
  const entryBits = [];
  if (ride.competitorNumber != null) entryBits.push(`#${ride.competitorNumber}`);
  if (ride.division) entryBits.push(ride.division);
  if (riderOnHorse) entryBits.push(riderOnHorse);

  const placing = placingLabel(ride.placing);
  const percentage = formatPercentage(ride.percentage);
  // Card #2229 — the DR123.3 terminal-state marker report-api now carries
  // (public-sheets.js `termReason`, sourced through the same
  // `deriveRideTermReason` derivation #2061 wired into dressage-result-model
  // for the printed class-results report, #2046 then proved that wiring):
  // one of "Scratched", "Excused",
  // "Eliminated", "No Show", or null for a ride that has not terminated.
  // USEF Rulebook Chapter DR (docs/rules/usef/rulebook-dr-dressage-2026.pdf),
  // DR123.3 (Scoring, Classification and Prize-Giving): "If a competitor
  // withdraws (scratches) prior to a class or is excused, eliminated or a
  // 'no show' ... the words 'scratched', 'excused', 'eliminated', or 'no
  // show' or abbreviations of each, must appear after the competitor's name
  // in the result sheet." A terminal ride is never getting scored, so it
  // must never fall into the scorePending branch below (recorded as a card
  // comment, #2229).
  const terminal = ride.termReason || null;
  // Card #2177 AC2/AC3 — a visible (class-placed) ride with neither a real
  // percentage NOR a placing yet is the no-score-yet state (the sheet was
  // rendered/cached before the ride's score landed). A place-only class
  // (#2211: DR204.2.c) legitimately carries `placing` with a permanently
  // null `percentage` — that ride still shows its placing chip alone, never
  // this "pending" text, so a real placement is never mislabeled as pending.
  // Card #2229 — a terminal ride (see above) is excluded here too: it will
  // never be scored, so "Score pending" would be actively misleading.
  const scorePending = !percentage && !placing && !terminal;

  // Card #1753 — a Dressage Seat Equitation sheet is never posted (USEF
  // Rulebook, Dressage Division, 2026 edition, DR133.1.n: "Judges are
  // encouraged to provide verbal comments to riders after the class, but
  // scoresheets must not be posted"), while DR133.1.j still requires the
  // rider be given a final percentage. So this replaces the View/Download
  // buttons ONLY — the percentage and placing above render exactly as they do
  // for any other ride, which is the whole point of the rule. report-api is
  // the real lock (it reports no available variant and refuses the /open
  // route outright); this is the explanation, so a rider sees a reason rather
  // than two dead buttons. Dressage Seat Medal is NOT suppressed and keeps
  // both buttons (DR133.2.g.4).
  const sheetSuppressed = Boolean(ride.scoreSheetSuppressed);
  const suppressionRule = ride.scoreSheetSuppressionRule || null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>
              {title}
            </Typography>
            {testName && (
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                Test: {testName}
              </Typography>
            )}
            {dateTimeBits.length > 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {dateTimeBits.join(" · ")}
              </Typography>
            )}
            {entryBits.length > 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {entryBits.join(" · ")}
              </Typography>
            )}
            {ride.sponsor && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                Class Sponsored by {ride.sponsor}
              </Typography>
            )}
          </Box>
          {(percentage || placing || scorePending || terminal) && (
            <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
              {percentage && (
                <Typography
                  variant="h5"
                  sx={{ color: "text.primary", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {percentage}
                </Typography>
              )}
              {/* Card #2229 — the DR123.3 outcome label, checked BEFORE
                  scorePending below so a terminal ride never renders "Score
                  pending" even when percentage/placing are both null.
                  `warning`/outlined mirrors the term-reason chip already
                  shipped on scoreadmin-fe's ClassPlacings page (#2058/#2059),
                  same vocabulary, same look, different surface. */}
              {terminal ? (
                <Chip
                  label={terminal}
                  size="small"
                  color="warning"
                  variant="outlined"
                  data-testid="ride-term-reason"
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                />
              ) : (
                /* Card #2177 AC2 — visible, distinguishable no-score-yet
                   state: never a fabricated "0.000%", never blank silence
                   either. */
                scorePending && (
                  <Typography
                    variant="body2"
                    data-testid="score-pending"
                    sx={{ color: "text.secondary", fontStyle: "italic", whiteSpace: "nowrap" }}
                  >
                    Score pending
                  </Typography>
                )
              )}
              {/* Card #2229 — DR123.3: placing is reserved for a horse that
                  receives an award; a terminated ride does not, so the
                  terminal chip above replaces the placing chip rather than
                  sitting beside it. */}
              {!terminal && placing && (
                <Chip
                  label={placing}
                  size="small"
                  sx={{
                    bgcolor: PLACING_BADGE_BG,
                    color: "#fff",
                    fontWeight: 700,
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
      <Divider />
      {sheetSuppressed ? (
        <CardActions sx={{ px: 2, py: 2 }}>
          {/* text.secondary, not MUI `primary`: the brand primary is a dark
              grey that goes near-invisible in dark mode. */}
          <Typography
            variant="body2"
            data-testid="scoresheet-suppressed"
            sx={{ color: "text.secondary" }}
          >
            Score sheets are not posted for this class
            {suppressionRule ? ` (${suppressionRule})` : ""}. Your score and
            placing are shown above; ask the judge for verbal comments after
            the class.
          </Typography>
        </CardActions>
      ) : (
      <CardActions sx={{ gap: 1, px: 2, py: 2 }}>
        {/* Brand orange, not MUI `primary` — the brand primary is a dark grey
            that goes near-invisible on a dark background, so these read as
            disabled in dark mode. */}
        <Button
          variant="contained"
          startIcon={<OpenInNewIcon />}
          component="a"
          href={htmlHref}
          target="_blank"
          rel="noopener"
          disabled={!htmlHref}
          onClick={() =>
            // #948 — no PII: variant + class number only, never rider name,
            // email, CNO, rideId, or the requestId/token itself.
            trackEvent("view_scoresheet", {
              variant: htmlVariant,
              class_number: ride.classNumber ?? null,
            })
          }
          sx={{
            bgcolor: colors.foxOrange,
            color: colors.darkRed,
            fontWeight: 700,
            "&:hover": { bgcolor: colors.foxOrange, filter: "brightness(0.92)" },
          }}
        >
          View
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          component="a"
          href={pdfHref}
          disabled={!pdfHref}
          onClick={() =>
            // #948 — no PII: see the View button's onClick above.
            trackEvent("download_pdf", {
              variant: pdfVariant,
              class_number: ride.classNumber ?? null,
            })
          }
          sx={{
            color: "text.primary",
            borderColor: "text.secondary",
            "&:hover": { borderColor: "text.primary" },
          }}
        >
          Download PDF
        </Button>
      </CardActions>
      )}
    </Card>
  );
}

// Card #1715 — plain ordinal for the provisional-standing line ("2nd of 5
// scored so far"), deliberately NOT `placingLabel` above: that helper appends
// "Place" and feeds the same navy final-result chip RideCard uses, both of
// which are exactly the "reads as a result" look this line must avoid.
function ordinal(n) {
  const v = n % 100;
  const suffixes = ["th", "st", "nd", "rd"];
  const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
  return `${n}${suffix}`;
}

function standingLine(standing) {
  const placing = Number(standing?.placing);
  const ofCount = Number(standing?.ofCount);
  if (!standing || !Number.isFinite(placing) || placing <= 0) {
    return "Not yet ranked — no confirmed scores in this class yet.";
  }
  // ofCount is how many rides in the class have been SCORED/confirmed so
  // far, not the class's total entry count — the card's AC is explicit this
  // must never read as "2nd of 5" (implying 5 total), only "so far".
  return Number.isFinite(ofCount) && ofCount > 0
    ? `${ordinal(placing)} of ${ofCount} scored so far`
    : `${ordinal(placing)} so far`;
}

// Card #1715 — a ride whose class hasn't been placed or closed yet. Shows
// movement-by-movement marks and a provisional standing (AC1/AC2), but
// deliberately carries NONE of RideCard's result-shaped chrome: no View/
// Download buttons (the full sheet is server-side locked until the class is
// placed, AC3 — this component has no artifact URL to offer even if it
// wanted to), no percentage chip, no placing chip. The "Provisional" caption
// is a visible sentence, not a subtle label, per AC2.
function PendingRideCard({ ride }) {
  // #1837 — never render foxbox's "FVD Test <id>" missing-test-map breadcrumb
  // on this public rider portal.
  const testName = dropFvdPlaceholderTestName(ride.testName);
  const testOrClassName =
    testName || ride.className || `Ride #${ride.rideNumber}`;
  const title = ride.classNumber != null
    ? `CL ${ride.classNumber}: ${testOrClassName}`
    : testOrClassName;

  const dateTimeBits = [ride.date, ride.time].filter(Boolean);
  const riderOnHorse = [ride.riderName, ride.horseName].filter(Boolean).join(" on ");
  const entryBits = [];
  if (ride.competitorNumber != null) entryBits.push(`#${ride.competitorNumber}`);
  if (ride.division) entryBits.push(ride.division);
  if (riderOnHorse) entryBits.push(riderOnHorse);

  const marks = ride.marks || [];

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>
              {title}
            </Typography>
            {testName && (
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                Test: {testName}
              </Typography>
            )}
            {dateTimeBits.length > 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {dateTimeBits.join(" · ")}
              </Typography>
            )}
            {entryBits.length > 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {entryBits.join(" · ")}
              </Typography>
            )}
          </Box>

          <Alert severity="info" sx={{ alignItems: "center" }}>
            Provisional — class not yet placed. Standing: {standingLine(ride.standing)}
          </Alert>

          {marks.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary" }}>Mvt</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>Description</TableCell>
                    <TableCell sx={{ color: "text.secondary" }} align="right">
                      Score
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marks.map((position) => (
                    <Fragment key={position.position}>
                      {(position.movements || []).map((m) => (
                        <TableRow key={`${position.position}-mv-${m.movementNumber}`}>
                          <TableCell sx={{ color: "text.primary" }}>
                            {m.movementNumber}
                          </TableCell>
                          <TableCell sx={{ color: "text.primary" }}>
                            {m.description}
                          </TableCell>
                          <TableCell sx={{ color: "text.primary" }} align="right">
                            {m.score ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(position.collectives || []).map((c) => (
                        <TableRow key={`${position.position}-col-${c.movementNumber}`}>
                          <TableCell sx={{ color: "text.primary" }}>
                            {c.movementNumber}
                          </TableCell>
                          <TableCell sx={{ color: "text.primary" }}>
                            {c.name}
                          </TableCell>
                          <TableCell sx={{ color: "text.primary" }} align="right">
                            {c.score ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function SheetsPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | notfound | generating | ready | failed | error
  const [data, setData] = useState(null);
  const [includeComments, setIncludeComments] = useState(true);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { status, body } = await fetchScoreSheet(token);
      if (status === 404) {
        setState("notfound");
        return "stop";
      }
      if (status < 200 || status >= 300) {
        setState("error");
        return "stop";
      }
      setData(body);
      if (body.status === "FAILED") {
        setState("failed");
        return "stop";
      }
      if (body.ready || body.status === "READY") {
        setState("ready");
        // #1080 — "ready" only means SOME sheets rendered, not that every
        // ride's class has been placed yet (report-api withholds a ride
        // until its class is placed and reports the rest via pendingCount).
        // Stopping the poll here unconditionally froze the "N of your rides
        // aren't shown yet" banner in place: a rider who loaded the page
        // before the last class was placed would never see it clear without
        // a manual refresh, even though report-api recomputes the real
        // state fresh on every request. Keep polling until pendingCount
        // actually reaches zero.
        return (body.pendingCount || 0) > 0 ? "continue" : "stop";
      }
      setState("generating");
      return "continue";
    } catch {
      setState("error");
      return "stop";
    }
  }, [token]);

  useEffect(() => {
    let active = true;
    const clear = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    load().then((outcome) => {
      if (!active) return;
      if (outcome === "continue" && !intervalRef.current) {
        intervalRef.current = setInterval(async () => {
          const next = await load();
          if (next === "stop") clear();
        }, POLL_INTERVAL_MS);
      }
    });

    return () => {
      active = false;
      clear();
    };
  }, [load]);

  if (state === "loading") {
    return (
      <PageShell>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (state === "notfound") {
    return (
      <PageShell>
        <Alert severity="warning">This link wasn&apos;t found.</Alert>
      </PageShell>
    );
  }

  if (state === "error") {
    return (
      <PageShell>
        <Alert severity="error">
          Something went wrong loading your score sheets. Please try again later.
        </Alert>
      </PageShell>
    );
  }

  if (state === "failed") {
    return (
      <PageShell>
        <Alert severity="error">
          We couldn&apos;t generate your score sheets. Please visit the show
          office for assistance.
        </Alert>
      </PageShell>
    );
  }

  if (state === "generating") {
    return (
      <PageShell>
        <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress />
          <Typography align="center" sx={{ color: "text.secondary" }}>
            Your score sheets are still being generated — this page will refresh
            automatically.
          </Typography>
        </Stack>
      </PageShell>
    );
  }

  // ready
  const rides = data?.rides || [];
  // #703 — a class isn't placed (closed) yet: report-api withholds that ride's
  // full sheet entirely (no urls of any variant). #1715: it now ALSO returns
  // that ride via `pendingRides` below with movement marks + a provisional
  // standing, so this count is no longer the only signal for a withheld ride.
  const pendingCount = data?.pendingCount || 0;
  // #1715 — the withheld rides themselves: movement marks + provisional
  // standing, never the full rendered sheet (see PendingRideCard).
  const pendingRides = data?.pendingRides || [];
  return (
    <PageShell>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
            {data?.riderName || "Your score sheets"}
          </Typography>
          {data?.showName && (
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              {data.showName}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Scoresheet View Preference
          </Typography>
          {/* Selected state is brand orange in BOTH themes. MUI's default
              selected tint is dimmer than the unselected label on a dark
              background, which made the active mode read as the inactive one. */}
          <ToggleButtonGroup
            value={includeComments ? "with" : "without"}
            exclusive
            onChange={(e, val) => {
              if (val !== null) setIncludeComments(val === "with");
            }}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                color: "text.secondary",
                borderColor: "divider",
              },
              "& .MuiToggleButton-root.Mui-selected": {
                bgcolor: colors.foxOrange,
                color: colors.darkRed,
                fontWeight: 700,
                "&:hover": { bgcolor: colors.foxOrange, filter: "brightness(0.92)" },
              },
            }}
          >
            <ToggleButton value="with">With comments</ToggleButton>
            <ToggleButton value="without">Without comments</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {pendingCount > 0 && (
          <Alert severity="info">
            {pendingCount === 1
              ? "One of your rides isn't shown yet — its class hasn't been placed, so its full sheet stays locked for now. You can see its marks and provisional standing below in the meantime."
              : `${pendingCount} of your rides aren't shown yet — those classes haven't been placed, so their full sheets stay locked for now. You can see their marks and provisional standing below in the meantime.`}
          </Alert>
        )}

        {rides.length === 0 && pendingRides.length === 0 && pendingCount === 0 ? (
          <Alert severity="info">
            No score sheets are available for this link yet.
          </Alert>
        ) : (
          // #1699 — grouped by day (chronological), each day's rides
          // ordered by ride time earliest first. See groupRidesByDay's own
          // header comment for why the day key is `ride.date` verbatim
          // rather than anything re-derived from a raw timestamp.
          <Stack spacing={3}>
            {groupRidesByDay(rides).map((group) => (
              <Box key={group.dayKey ?? "other"}>
                <Typography
                  variant="subtitle1"
                  data-testid="day-heading"
                  sx={{ color: "text.primary", fontWeight: 700, mb: 1 }}
                >
                  {group.heading}
                </Typography>
                <Stack spacing={2}>
                  {group.rides.map((ride) => (
                    <RideCard
                      key={ride.rideId}
                      ride={ride}
                      includeComments={includeComments}
                      requestId={token}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
            {pendingRides.map((ride) => (
              <PendingRideCard key={ride.rideId} ride={ride} />
            ))}
          </Stack>
        )}
      </Stack>
    </PageShell>
  );
}
